import { useRef } from "react";

import TablePagination from "components/Table/Pagination";
import TableSimpleHead from "ds/components/TableSimple/Head";
import { IntentResource } from "types/generated";
import usePagination from "components/Table/Pagination/usePagination";
import useIntentProjectResourcesStore from "shared/IntentProject/useSearchIntentResources/store";
import TableWrapper from "ds/components/Table/Wrapper";
import Table from "ds/components/Table";
import TableBody from "ds/components/Table/Body";
import TableSkeleton from "ds/components/Table/Skeleton";

import useIntentProjectResourcesTableStore from "./useResourcesTableStore";
import {
  INTENT_PROJECT_RESOURCES_LIMIT,
  INTENT_PROJECT_RESOURCES_LIMIT_STORAGE_KEY,
  INTENT_PROJECT_RESOURCES_TABLE_FILTERS_STORAGE_KEY,
  INTENT_PROJECT_PAGINATION_OPTIONS,
} from "./constants";
import INTENT_PROJECT_RESOURCES_TABLE_CELLS from "./Cells";
import IntentProjectResourcesTableRows from "./Rows";

const ariaLabel = "Intent Project Resources table";

type IntentProjectResourcesTableProps = {
  resources: IntentResource[];
  loading: boolean;
  startCursor: string | undefined;
  endCursor: string | undefined;
  allItemsCount: number;
  resetKey?: unknown;
};

const IntentProjectResourcesTable = ({
  resources,
  loading,
  startCursor,
  endCursor,
  allItemsCount,
  resetKey,
}: IntentProjectResourcesTableProps) => {
  const tableRef = useRef<HTMLDivElement>(null);

  const { onFetchItems } = useIntentProjectResourcesStore();

  const {
    rowsPerPage,
    setRowsPerPage,
    from,
    to,
    lastPage,
    page,
    goToNextPage,
    goToPrevPage,
    goToFirstPage,
    goToLastPage,
  } = usePagination({
    fetchItems: onFetchItems,
    startCursor,
    endCursor,
    allItemsCount,
    visibleItemsCount: resources.length,
    initialLimit: INTENT_PROJECT_RESOURCES_LIMIT,
    storageKey: INTENT_PROJECT_RESOURCES_LIMIT_STORAGE_KEY,
    resetKey,
    loading,
  });

  const columnIds = useIntentProjectResourcesTableStore((state) => state.columnsOrder.visible);

  return (
    <>
      <TableWrapper ref={tableRef} hasPagination>
        <Table ariaLabel={ariaLabel}>
          <TableSimpleHead
            store={useIntentProjectResourcesTableStore}
            ariaLabel={ariaLabel}
            filtersStorageKey={INTENT_PROJECT_RESOURCES_TABLE_FILTERS_STORAGE_KEY}
            tableRef={tableRef}
          />
          {loading && <TableSkeleton columnIds={columnIds} rowsPerPage={rowsPerPage} />}

          <TableBody>
            <IntentProjectResourcesTableRows
              data={resources}
              store={useIntentProjectResourcesTableStore}
              cellPerColumn={INTENT_PROJECT_RESOURCES_TABLE_CELLS}
              tableRef={tableRef}
            />
          </TableBody>
        </Table>
      </TableWrapper>

      <TablePagination
        from={from}
        to={to}
        loading={loading}
        goToNextPage={goToNextPage}
        setRowsPerPage={setRowsPerPage}
        goToPrevPage={goToPrevPage}
        goToFirstPage={goToFirstPage}
        goToLastPage={goToLastPage}
        rowsPerPage={rowsPerPage}
        itemsCount={allItemsCount}
        lastPage={lastPage}
        page={page}
        paginationOptions={INTENT_PROJECT_PAGINATION_OPTIONS}
      />
    </>
  );
};

export default IntentProjectResourcesTable;
